<template>
  <content-dialog
    ref="dialog"
    v-model="dialogOpen"
    fullscreen-tablet
    transition-show="scale"
    height="75vh"
    :width="`${768 + (filterDrawerOpen ? filterDrawerWidth : 0)}px`"
    :loading="loading"
  >
    <template #card>
      <q-layout
        container
        view="hHr lpR fFr"
      >
        <q-header
          v-if="$refs.dialog"
          class="blurred-container text-black"
        >
          <q-toolbar>
            <q-btn
              v-close-popup
              dense
              flat
              round
              icon="mdi-arrow-left"
            />
            <page-toolbar-title>
              {{ dialogTitleStr }}
            </page-toolbar-title>
            <q-btn
              flat
              round
              dense
              icon="mdi-filter-variant"
              class="q-mr-sm"
              @click="toggleFiltersDrawer"
            >
              <q-tooltip>Toggle Filters</q-tooltip>
            </q-btn>
          </q-toolbar>

          <q-toolbar class="bg-transparent q-px-md">
            <q-input
              v-model="filterVals.search"
              autofocus
              placeholder="Search Appointments..."
              type="search"
              dense
              outlined
              clearable
              style="min-width: 225px;"
              :loading="loading"
              :debounce="1000"
            >
              <template #prepend>
                <q-icon name="search" />
              </template>
            </q-input>
            <q-space />
            <q-item-label
              class="text-right text-caption"
              style="width: 100px;"
            >
              {{ commaNumber(total) }} {{ pluraliseString('result', total) }}
            </q-item-label>
          </q-toolbar>
        </q-header>

        <q-page-container>
          <q-page>
            <clickable-item
              v-if="canCreateAppointment"
              label="Create New Appointment"
              @click="$refs.appointmentAddDialog.open(null, null, restrictToClients || [])"
            />

            <dynamic-table
              v-model:page="filterVals.page"
              :headers="[]"
              :list="results"
              :showing-results="filterVals.rows"
              :total-results="total"
              :loading="loading"
              separator
              clickable
              pagination
              responsive-width="none"
            >
              <template #row="{ row }">
                <appointment-item
                  :appointment="row"
                  clickable
                  with-color
                  @click="onClickAppointment(row)"
                >
                  <template v-if="showSelection" #default>
                    <component
                      :is="multiple ? 'q-checkbox' : 'q-radio'"
                      :model-value="isResultSelected(row.id)"
                      :val="true"
                      dense
                      class="no-pointer-events"
                    />
                  </template>
                </appointment-item>
              </template>

              <template #loading>
                <q-list separator>
                  <q-item-wrapper
                    v-for="i in 5"
                    :key="i"
                  >
                    <q-skeleton type="text" class="text-subtitle1" />
                    <q-skeleton type="text" class="text-caption" />
                  </q-item-wrapper>
                </q-list>
              </template>
            </dynamic-table>
          </q-page>
        </q-page-container>

        <q-drawer
          v-model="filterDrawerOpen"
          :width="filterDrawerWidth"
          :breakpoint="filterBreakpoint"
          side="right"
          show-if-above
          class="bg-drawer"
        >
          <SelectFilters
            ref="drawer"
            :filter-vals="filterVals"
            :is-scheduled="isScheduled"
            :restricted-to-client="hasRestrictedClients"
            :restricted-to-attendees="hasRestrictedAttendees"
            :restricted-to-types="hasRestrictedTypes"
            @apply-filters="applyFilters"
            @scroll-to-bottom="scrollDrawerToBottom"
          />
        </q-drawer>
      </q-layout>
    </template>
  </content-dialog>

  <AppointmentAddDialog
    ref="appointmentAddDialog"
    model-payload
    @created="onCreateAppointment"
  />
</template>

<script>
import searchMixin from 'components/app/calendar/partials/mixins/searchMixin'
import input from 'src/helpers/input'

import SelectFilters from 'components/app/calendar/partials/SelectFilters.vue'
import AppointmentAddDialog from 'components/app/calendar/Add'

function defaultFilterVals () {
  return {
    page: 1,
    rows: 20,
    sortBy: 'closest',
    sortDirection: 'asc',
    search: null,
    status: null,
    startDate: null,
    endDate: null,
    allDay: false,
    color: null,
    timezone: null,
    clients: [],
    attendees: [],
    appointmentTypes: [],
    creator: null
  }
}

export default {
  components: {
    SelectFilters, AppointmentAddDialog
  },
  mixins: [searchMixin, input],
  props: {
    modelValue: {
      type: [Array, Object],
      default: () => {
        return []
      }
    },
    showSelection: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    isScheduled: {
      type: Boolean,
      default: false
    },
    restrictToClients: {
      type: Array,
      default: null
    },
    restrictToAttendees: {
      type: Array,
      default: null
    },
    restrictToTypes: {
      type: Array,
      default: null
    },
    // Allow user to create appointment directly within dialog
    canCreate: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialogOpen: false,
      filterBreakpoint: 750,
      filterDrawerWidth: 300,
      filterDrawerOpen: false,
      filterTimer: null,
      filterVals: defaultFilterVals()
    }
  },
  computed: {
    dialogTitleStr () {
      if (this.showSelection) {
        if (this.multiple) {
          return 'Select Appointments'
        }
        return 'Select Appointment'
      }
      return 'Search Appointments'
    },
    hasRestrictedClients () {
      return this.restrictToClients && Array.isArray(this.restrictToClients) && this.restrictToClients.length > 0
    },
    hasRestrictedAttendees () {
      return this.restrictToAttendees && Array.isArray(this.restrictToAttendees) && this.restrictToAttendees.length > 0
    },
    hasRestrictedTypes () {
      return this.restrictToTypes && Array.isArray(this.restrictToTypes) && this.restrictToTypes.length > 0
    },
    canCreateAppointment () {
      return this.canCreate && this.showSelection
    }
  },
  watch: {
    'filterVals.page' () {
      try {
        this.$refs.dialog.scrollTop()
      }
      catch (err) {}
      this.loadAppointments()
    },
    'filterVals.search' () {
      this.loadToPageOne()
    }
  },
  methods: {
    scrollDrawerToBottom (newAdvancedFilterVal) {
      if (!newAdvancedFilterVal) {
        return
      }
      // Scroll to bottom of filters drawer to show advanced options
      this.timed(() => {
        try {
          this.$refs.drawer.$el.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
          })
        }
        catch (err) {}
      }, 500)
    },
    onClickAppointment (appointment) {
      this.onResultClick(appointment)

      if (this.showSelection && !this.multiple) {
        this.timed(() => {
          this.dialogOpen = false
        }, 250)
      }
    },
    toggleFiltersDrawer () {
      this.filterDrawerOpen = !this.filterDrawerOpen
    },
    applyFilters () {
      this.loadToPageOne()
      if (this.windowWidth <= this.filterBreakpoint) {
        this.toggleFiltersDrawer()
      }
    },
    loadToPageOne () {
      if (this.filterVals.page !== 1) {
        this.filterVals.page = 1
      }
      else {
        this.loadAppointments()
      }
    },
    onCreateAppointment ({ parent }) {
      this.onClickAppointment(parent)
    },
    loadAppointments () {
      this.doSearch({
        filter: this.filterVals.search,
        page: this.filterVals.page,
        rows: this.filterVals.rows,
        sortBy: this.filterVals.sortBy,
        sortDirection: this.filterVals.sortDirection,
        status: this.filterVals.status,
        start: this.filterVals.startDate,
        end: this.filterVals.endDate,
        allDay: this.filterVals.allDay,
        color: this.filterVals.color,
        timezone: this.filterVals.timezone,
        isScheduled: this.isScheduled,
        clients: this.filterVals.clients.map(item => item.id),
        users: this.filterVals.attendees.map(item => item.id),
        appointmentTypes: this.filterVals.appointmentTypes.map(item => item.id),
        creator: this.filterVals.creator ? this.filterVals.creator.id : null
      })
    },
    open () {
      this.filterVals = defaultFilterVals()
      this.dialogOpen = true

      if (this.hasRestrictedClients) {
        this.filterVals.clients = this.restrictToClients
      }

      if (this.hasRestrictedAttendees) {
        this.filterVals.attendees = this.restrictToAttendees
      }

      if (this.hasRestrictedTypes) {
        this.filterVals.appointmentTypes = this.restrictToTypes
      }

      this.$nextTick(() => {
        this.loadAppointments()
      })
    }
  }
}
</script>
